"use client";

import Link from "next/link";
import Button from "../../ui/Button";
import Image from "next/image";
import BenefitCard from "@/components/ui/BenefitCard";
import ShopAllIcon from "@/components/icons/ShopAllIcon";
import StorefrontIcon from "@/components/icons/StorefrontIcon";
import HeadsetIcon from "@/components/icons/HeadsetIcon";
import FlameFilledIcon from "@/components/icons/FlameFilledIcon";
import AwardIcon from "@/components/icons/AwardIcon";
import DeliveryTruckIcon from "@/components/icons/DeliveryTruckIcon";

const FINEST_BENEFITS = [
  {
    icon: <ShopAllIcon size={24} className="h-6 w-6 text-blue-80" />,
    title: "Largest Selection",
    description:
      "An expertly curated menu featuring the best cannabis brands and premium products",
  },
  {
    icon: <StorefrontIcon size={24} className="h-6 w-6 text-blue-80" />,
    title: "Retail Experience",
    description:
      "A simple, stress-free ordering process online or in-store",
  },
  {
    icon: <HeadsetIcon size={24} className="h-6 w-6 text-blue-80" />,
    title: "Customer Service",
    description:
      "Our team knows their stuff and is happy to help, in-store or online",
  },
  {
    icon: <FlameFilledIcon size={24} className="h-6 w-6 text-blue-80" />,
    title: "Fair Pricing",
    description: "High-quality cannabis at competitive prices across every budget range",
  },
  {
    icon: <AwardIcon size={24} className="h-6 w-6 text-blue-80" />,
    title: "Dank Club",
    description:
      "Earn points on every purchase and swap them for exclusive rewards and discounts",
  },
  {
    icon: <DeliveryTruckIcon size={24} className="h-6 w-6 text-blue-80" />,
    title: "Convenience",
    description:
      "Shop in-store, click & collect, or enjoy fast delivery across Alberta",
  },
] as const;

export default function WhyDankFinestSection() {
  return (
    <section className="w-full" aria-labelledby="why-dank-finest-heading">
      <div className="w-full">
        <h2
          id="why-dank-finest-heading"
          className="mb-4 text-h3-mobile font-bold text-text-primary sm:mb-8 sm:text-h3-desktop"
        >
          Dank&apos;s finest
        </h2>

        <div className="grid grid-cols-1 gap-2 sm:gap-4 md:grid-cols-2 lg:grid-cols-3">
          {FINEST_BENEFITS.map((b) => (
            <BenefitCard key={b.title} icon={b.icon} title={b.title} description={b.description} />
          ))}
        </div>

        {/* CTA Banner, same block as `BenefitsSection` */}
        <div className="relative mt-6 w-full overflow-hidden rounded-2xl bg-[#FFD2D2]">
          <div className="absolute inset-0">
            <Image
              src="/media/sections/lifestyle/backgrounds/banner-background-shape.webp"
              alt=""
              fill
              className="object-cover"
              aria-hidden="true"
            />
          </div>

          <div className="relative z-10 flex flex-col items-center justify-between gap-4 px-4 pb-4 sm:px-6 sm:pb-6 lg:flex-row">
            <div className="flex h-48 w-full items-center justify-center overflow-hidden sm:hidden">
              <Image
                src="/media/sections/lifestyle/backgrounds/banner-image.webp"
                alt=""
                width={220}
                height={300}
                className="w-full object-center"
                aria-hidden="true"
              />
            </div>
            <div className="-mb-8 h-32 overflow-hidden max-sm:hidden">
              <Image
                src="/media/sections/lifestyle/backgrounds/banner-image.webp"
                alt=""
                width={220}
                height={300}
                className="-mt-6 object-top"
                aria-hidden="true"
              />
            </div>
            <div className="flex-1 sm:mt-7">
              <h2 className="mb-1 text-body-20 font-bold leading-tight text-text-primary">
                Enjoy a high country life
              </h2>
              <p className="text-body-16 font-medium leading-relaxed text-text-secondary">
                Gummies, chocolates and drinks from Canada&apos;s best makers, ready for pickup or same day delivery.
              </p>
            </div>

            <Button asChild variant="primary" size="lg" className="sm:w-fit w-full px-6 py-3 rounded-xl mt-6">
              <Link href="/search?category=Edibles">
                Shop our new edibles
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
